import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Typography,
  Skeleton,
  Button,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceIcon from "@mui/icons-material/Place";
import announcementServices from "../../services/announcementServices";
import { baseURL } from "../../services/api";
import { fDateTime, fTime } from "../../utils";
import ImageWithFallback from "../../components/ImageWithFallback";
import "./AnnouncementDetail.css";
import "../../components/RichTextEditor/richtext.css";

const ContentWrap = styled(Box)({
  maxWidth: 1200,
  margin: "0 auto",
  padding: "0 24px",
});

const getFileType = (url) => url?.split(".").pop()?.toLowerCase();

const AnnouncementDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    announcementServices
      .getByID(id)
      .then((res) => setData(res?.data?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ py: 6 }}>
        <ContentWrap>
          <Skeleton height={40} width={200} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2, borderRadius: 1 }} />
          <Skeleton height={32} sx={{ mt: 2 }} />
          <Skeleton height={24} width="80%" />
        </ContentWrap>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <ContentWrap>
          <Typography variant="h6" color="text.secondary">Announcement not found.</Typography>
          <Button component={Link} to="/announcements" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
            Back to Announcements
          </Button>
        </ContentWrap>
      </Box>
    );
  }

  const isPdf = getFileType(data?.url) === "pdf";

  return (
    <Box sx={{ pb: 6, pb: 2, minHeight: "60vh" }}>
      <ContentWrap>
        <Button
          component={Link}
          to="/announcements"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2, mb: 1 }}
        >
          Back to Announcements
        </Button>

        <Typography variant="h4" fontWeight={700} color="#1a80c8" gutterBottom>
          {data.title}
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2, color: "#555" }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CalendarTodayIcon sx={{ fontSize: 18 }} />
            <span>{fDateTime(data?.date)}</span>
          </Stack>
          {data?.time && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <ScheduleIcon sx={{ fontSize: 18 }} />
              <span>{fTime(data?.time)}</span>
            </Stack>
          )}
          {data?.location && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <PlaceIcon sx={{ fontSize: 18 }} />
              <span>{data.location}</span>
            </Stack>
          )}
        </Stack>

        {data?.url && (
          <Box
            sx={{
              mb: 3,
              width: "100%",
              maxWidth: 800,
              height: 480,
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isPdf ? (
              <a href={baseURL + data.url} target="_blank" rel="noopener noreferrer">
                <img src="/pdf.png" alt="PDF" style={{ maxHeight: 300, borderRadius: 8 }} />
              </a>
            ) : (
              <ImageWithFallback
                path={data.url}
                alt={data.title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                  borderRadius: "12px",
                }}
              />
            )}
          </Box>
        )}

        {data?.description && (
          <Box
            className="announcement-detail-description rich-text-content"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
        )}
      </ContentWrap>
    </Box>
  );
};

export default AnnouncementDetail;
